/**
 * useViewport — matchMedia-backed viewport size hook (milestone v1.4 / Plan 04-10).
 *
 * Returns `{ isMobile, isTablet, isDesktop }` booleans derived from the three
 * IA §9 breakpoints:
 *
 *   - mobile  : `(max-width: 767px)`                 — iPhone SE class, filter bar collapses
 *   - tablet  : `(min-width: 768px) and (max-width: 1023px)` — iPad portrait / small laptop
 *   - desktop : `(min-width: 1024px)`                — full dashboard layout
 *
 * SSR-safe: when `window` is undefined (server render, vitest+jsdom before
 * setup, etc.) the hook returns `{ isDesktop: true, isMobile: false, isTablet:
 * false }` so the initial paint renders the desktop variant. Client-side, the
 * effect subscribes to `change` events on all three MediaQueryLists and
 * re-reads `.matches` into a single state object so a resize across a
 * breakpoint flips exactly one render.
 *
 * Callers: FilterBar (renders button variant when `isMobile`), and any other
 * component that needs to branch on viewport. Do NOT use this for simple
 * Tailwind styling — Tailwind's `md:`/`lg:` prefixes handle that — it's for
 * *structural* differences (different component shape, not just styling).
 */

import { useEffect, useState } from 'react';

export interface Viewport {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

const MOBILE_QUERY = '(max-width: 767px)';
const TABLET_QUERY = '(min-width: 768px) and (max-width: 1023px)';
const DESKTOP_QUERY = '(min-width: 1024px)';

function readViewport(): Viewport {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    // SSR / test environment without matchMedia — desktop-first default so
    // the server markup matches the most common client reality.
    return { isMobile: false, isTablet: false, isDesktop: true };
  }
  return {
    isMobile: window.matchMedia(MOBILE_QUERY).matches,
    isTablet: window.matchMedia(TABLET_QUERY).matches,
    isDesktop: window.matchMedia(DESKTOP_QUERY).matches,
  };
}

export function useViewport(): Viewport {
  const [viewport, setViewport] = useState<Viewport>(readViewport);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mobile = window.matchMedia(MOBILE_QUERY);
    const tablet = window.matchMedia(TABLET_QUERY);
    const desktop = window.matchMedia(DESKTOP_QUERY);

    // One handler for all three — any change re-reads the full viewport state
    // so the returned object is always internally consistent (exactly one of
    // the three booleans is true, except at the 767/768 and 1023/1024 boundaries
    // where matchMedia is deterministic per spec).
    const handleChange = () => {
      setViewport({
        isMobile: mobile.matches,
        isTablet: tablet.matches,
        isDesktop: desktop.matches,
      });
    };

    mobile.addEventListener('change', handleChange);
    tablet.addEventListener('change', handleChange);
    desktop.addEventListener('change', handleChange);

    // Re-sync once on mount — the initial `readViewport()` call ran before the
    // effect, which on StrictMode double-mount or SSR hydration could be stale.
    handleChange();

    return () => {
      mobile.removeEventListener('change', handleChange);
      tablet.removeEventListener('change', handleChange);
      desktop.removeEventListener('change', handleChange);
    };
  }, []);

  return viewport;
}
